import React, { useId } from 'react';

/**
 * The coin. ONE definition of it.
 *
 * Drawn in two places — the burst on a completed purchase, and the prompt that
 * explains a page is paid for in points — and those two must be the same
 * object. A second hand-drawn coin somewhere else is how a product ends up
 * with two currencies that look nothing alike.
 *
 * ⚠️ IT IS THE BRAND'S COIN, NOT A GENERIC ONE. It was gold, on the reasoning
 * that gold is what a coin is — but a gold disc on a SailLab screen is the one
 * thing on the page belonging to no palette at all, and points are OUR
 * currency, not a metal. The tones below come straight from `primary` in
 * `tailwind.config.js`; change them there and change them here.
 *
 * `variant` picks the GLYPH and nothing else. Points are a star struck into
 * the coin; a plan is a tick, because a plan pays out access rather than
 * currency. Never show the star for a subscription — it pictures something the
 * buyer did not receive.
 *
 * Decorative by default (`aria-hidden`), because everywhere it appears the
 * sentence beside it already says what it means. Pass a `title` only where the
 * mark is genuinely carrying information on its own.
 */

export type CoinVariant = 'points' | 'plan';

/**
 * primary-500 / primary-300 / primary-700, verbatim. Three tones because that
 * is what reads as a struck disc rather than a flat circle — the light one is
 * the top of the gradient and the halo, the dark one the inner rim.
 *
 * ⚠️ NOT the host's `--brand-color`. This mark says SailLab points, which are
 * the same thing on every host's page; painting it with whichever colour the
 * host chose would make one currency look like a different one per booking
 * page.
 */
export const COIN_COLORS = { face: '#2E7AA0', light: '#75BFDF', dark: '#1F5275' };

interface CoinMarkProps {
  size?: number;
  variant?: CoinVariant;
  /**
   * The highlight that sweeps the face. Only for the celebration, where the
   * coin is the subject; on a small icon it is noise the eye cannot resolve.
   */
  shine?: boolean;
  className?: string;
  title?: string;
}

const CoinMark: React.FC<CoinMarkProps> = ({
  size = 76,
  variant = 'points',
  shine = false,
  className,
  title,
}) => {
  // Gradient and clip ids must be unique per instance — two coins on one page
  // sharing an id makes the second one reference the first one's definition.
  const uid = useId().replace(/:/g, '');
  const { face, light, dark } = COIN_COLORS;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 76 76"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={`coin-face-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={light} />
          <stop offset="100%" stopColor={face} />
        </linearGradient>
        <clipPath id={`coin-clip-${uid}`}>
          <circle cx="38" cy="38" r="30" />
        </clipPath>
      </defs>

      {/* Halo, rim, face — three tones, which is what reads as metal rather
          than as a flat dot. */}
      <circle cx="38" cy="38" r="34" fill={light} opacity="0.25" />
      <circle cx="38" cy="38" r="30" fill={`url(#coin-face-${uid})`} />
      <circle cx="38" cy="38" r="24" fill="none" stroke={dark} strokeWidth="1.5" opacity="0.5" />

      {variant === 'plan' ? (
        // A tick, for access granted.
        <path
          d="M27 39l7.5 7.5L50 31"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        // A star struck into the coin. A currency symbol would name a
        // currency, and points are not one.
        <path
          d="M38 24l4 11h11.5l-9.3 6.8 3.6 11L38 46l-9.8 6.8 3.6-11-9.3-6.8H34z"
          fill="#FFFFFF"
          opacity="0.92"
        />
      )}

      {shine && (
        <g clipPath={`url(#coin-clip-${uid})`}>
          <rect
            className="celebration-shine"
            x="30"
            y="2"
            width="10"
            height="72"
            fill="#FFFFFF"
            opacity="0"
            transform="rotate(20 38 38)"
          />
        </g>
      )}
    </svg>
  );
};

export default CoinMark;
